import { useEffect, useRef, useState } from 'react';

import { format } from 'date-fns';

import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

import { Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@/components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

import type { Branch } from '@/models/Branchs';
import type { Level } from '@/models/Level';
import type { Lesson } from '@/models/Lessons';

const LessonsAdminComponent = () => {
	const [lessons, setLessons] = useState<Lesson[]>([]);
	useEffect(() => {
		const fetchLessons = async () => {
			try {
				const response = await fetch('/api/lessons');

				// /api/lessons answers 401 with an error object, not an array. Storing
				// that unchecked is what made the table crash on .filter, so bail out
				// and send an unauthenticated admin to the login page instead.
				if (response.status === 401) {
					localStorage.setItem('appContext', JSON.stringify({ isLogged: false, user: ' ', email: ' ', role: 'USER' }));
					window.location.replace('/login');
					return;
				}
				if (!response.ok) throw new Error(`HTTP ${response.status}`);

				setLessons(await response.json());
			} catch (error) {
				console.error('Failed to fetch lessons:', error);
			}
		};
		fetchLessons();
	}, []);

	const [_, setBranchs] = useState<Branch[]>([]);
	useEffect(() => {
		const fetchBranchs = async () => {
			try {
				const response = await fetch('/api/branchs');
				const data = await response.json();
				setBranchs(data);
			} catch (error) {
				console.error('Failed to fetch branchs:', error);
			}
		};
		fetchBranchs();
	}, []);

	const [levels, setLevels] = useState<string[]>([]);
	useEffect(() => {
		const fetchLevels = async () => {
			try {
				const response = await fetch('/api/levels');
				const data = (await response.json()) as Level[];
				let levelList: string[] = data.map((l) => l.title);
				levelList.push('الكل');
				setLevels(levelList);
			} catch (error) {
				console.error('Failed to fetch levels:', error);
			}
		};
		fetchLevels();
	}, []);

	const [selectedLevel, setSelectedLevel] = useState('الكل');
	const [searchQuery, setSearchQuery] = useState('');

	const filteredLessons = lessons.filter((l) => {
		const matchLevel = selectedLevel === 'الكل' || l.level === selectedLevel;
		const matchSearch = l.title.includes(searchQuery) || l.branch.includes(searchQuery);
		return matchLevel && matchSearch;
	});

	const [lessonFormData, setLessonFormData] = useState({
		title: '',
		description: '',
		keys_word: '',
		level: '3 ثانوي',
		branch: 'رياضيات',
		type: 'فيديو',
		duration: 0,
		path: '-',
		status: 'VALID',
	});

	// Add lesson
	const [isAddLessonDialogOpen, setIsAddLessonDialogOpen] = useState(false);
	const doOpenAddLessonDialog = () => {
		setLessonFormData({
			title: '',
			description: '',
			keys_word: '',
			level: '3 ثانوي',
			branch: 'رياضيات',
			type: 'فيديو',
			duration: 0,
			path: '-',
			status: 'VALID',
		});
		setIsAddLessonDialogOpen(true);
	};
	// Every write below is awaited before the list is re-read. They used to fire
	// the request without awaiting and refetch immediately, so the table showed
	// pre-write data and the change only appeared after a manual refresh.
	const refreshLessons = async () => {
		try {
			const response = await fetch('/api/lessons');
			if (!response.ok) throw new Error(`HTTP ${response.status}`);
			setLessons(await response.json());
		} catch (error) {
			console.error('Failed to fetch lessons:', error);
		}
	};

	const handleAddLessonSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		try {
			const response = await fetch('/api/lessons', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(lessonFormData),
			});
			if (!response.ok) throw new Error(`HTTP ${response.status}`);

			await refreshLessons();
			setIsAddLessonDialogOpen(false);
		} catch (error) {
			console.error('Failed to add lesson:', error);
		}
	};

	// Update lesson
	const [isUpdateLessonDialogOpen, setIsUpdateLessonDialogOpen] = useState(false);
	const [lessonUpdate, setLessonUpdate] = useState<Lesson>({
		id: 0,
		create_at: new Date(),
		title: '',
		description: '',
		keys_word: '',
		level: '',
		branch: '',
		type: '',
		duration: 0,
		path: '-',
		status: 'VALID',
	});
	const doOpenUpdateLessonDialog = (id: number) => {
		const fetchLessonById = async () => {
			try {
				const response = await fetch('/api/lessons/' + id);
				const lesson = await response.json();
				setLessonUpdate(lesson);
				setIsUpdateLessonDialogOpen(true);
			} catch (error) {
				console.error('Failed to fetch lessons:', error);
			}
		};
		fetchLessonById();
	};
	const handleUpdateLessonSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		try {
			const response = await fetch('/api/lessons', {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(lessonUpdate),
			});
			if (!response.ok) throw new Error(`HTTP ${response.status}`);

			await refreshLessons();
			setIsUpdateLessonDialogOpen(false);
		} catch (error) {
			console.error('Failed to update lesson:', error);
		}
	};

	// Delete lesson
	const [lessonToDeleteId, setLessonToDeleteId] = useState(0);
	const [isDeleteLessonDialogOpen, setIsDeleteLessonDialogOpen] = useState(false);
	const doShowDeleteLessonDialog = (id: number) => {
		setLessonToDeleteId(id);
		setIsDeleteLessonDialogOpen(true);
	};
	const doDeleteLesson = async () => {
		try {
			const response = await fetch('/api/lessons/' + lessonToDeleteId, {
				method: 'DELETE',
			});
			if (!response.ok) throw new Error(`HTTP ${response.status}`);

			await refreshLessons();
			setLessonToDeleteId(0);
			setIsDeleteLessonDialogOpen(false);
		} catch (error) {
			console.error('Failed to delete lesson:', error);
		}
	};

	// Show lesson details
	const [lessonDetailsId, setLessonDetailsId] = useState(0);
	const [lessonDetails, setLessonDetails] = useState<Lesson>({
		id: 0,
		create_at: new Date(),
		title: '',
		description: '',
		keys_word: '',
		level: '',
		branch: '',
		type: '',
		duration: 0,
		path: '',
		status: '',
	});
	const [isLessonDetailsDialogOpen, setIsLessonDetailsDialogOpen] = useState(false);
	const doShowLessonDetails = (id: number) => {
		setLessonDetailsId(id);
		setLessonDetails({
			id: 0,
			create_at: new Date(),
			title: '',
			description: '',
			keys_word: '',
			level: '',
			branch: '',
			type: '',
			duration: 0,
			path: '',
			status: '',
		});

		const fetchLessonById = async () => {
			try {
				const response = await fetch('/api/lessons/' + id);
				const lesson = await response.json();
				setLessonDetails(lesson);
				setIsLessonDetailsDialogOpen(true);
			} catch (error) {
				console.error('Failed to fetch lessons:', error);
			}
		};
		fetchLessonById();
	};

	// Upload Content
	const [isUploadLessonDialogOpen, setIsUploadLessonDialogOpen] = useState(false);
	const [isUploadLessonStart, setIsUploadLessonStart] = useState(false);
	const [lessonUploadBtnText, setLessonUploadBtnText] = useState('تحديث');
	const [lessonUploadId, setLessonUploadId] = useState(0);
	const [lessonUploadTitle, setLessonUploadTitle] = useState('');
	const [lessonUploadType, setLessonUploadType] = useState('فيديو');
	const [lessonContentFile, setLessonContentFile] = useState<File | null>(null);
	const [lessonUploadError, setLessonUploadError] = useState('');
	const [lessonUploadPercent, setLessonUploadPercent] = useState(0);
	const [lessonUploadSentBytes, setLessonUploadSentBytes] = useState(0);
	// Held so the cancel button can abort a transfer that is already in flight.
	const lessonUploadXhr = useRef<XMLHttpRequest | null>(null);

	const formatBytes = (bytes: number) => {
		if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} ك.ب`;
		if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} م.ب`;
		return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} غ.ب`;
	};

	const handleLessonContentChange = (e: any) => {
		setLessonUploadError('');
		setLessonContentFile(e.target.files[0] ?? null);
	};
	const doShowLessonUpload = (id: number, title: string, type: string) => {
		setIsUploadLessonStart(false);
		setLessonUploadBtnText('تحديث');
		setLessonUploadId(id);
		setLessonUploadTitle(title);
		setLessonUploadType(type);
		// The dialog is reused across lessons, so a file picked last time would
		// otherwise still be staged and get uploaded against the wrong lesson.
		setLessonContentFile(null);
		setLessonUploadError('');
		setLessonUploadPercent(0);
		setLessonUploadSentBytes(0);
		setIsUploadLessonDialogOpen(true);
	};

	const doUploadLessonContent = () => {
		if (!lessonContentFile) {
			setLessonUploadError('الرجاء اختيار ملف');
			return;
		}

		setIsUploadLessonStart(true);
		setLessonUploadError('');
		setLessonUploadBtnText('جاري التحديث ...');
		setLessonUploadPercent(0);
		setLessonUploadSentBytes(0);

		const url = '/api/lessons/upload/' + lessonUploadId + '/type/' + lessonUploadType;

		// fetch() exposes no upload progress whatsoever, so the bar needs XHR.
		// The body is the raw file rather than FormData: the server streams it
		// straight to disk, where multipart would have to be buffered first.
		const xhr = new XMLHttpRequest();
		lessonUploadXhr.current = xhr;

		const fail = (message: string) => {
			lessonUploadXhr.current = null;
			setLessonUploadError(message);
			setIsUploadLessonStart(false);
			setLessonUploadBtnText('تحديث');
			setLessonUploadPercent(0);
			setLessonUploadSentBytes(0);
		};

		xhr.upload.onprogress = (event) => {
			setLessonUploadSentBytes(event.loaded);
			if (event.lengthComputable) setLessonUploadPercent(Math.round((event.loaded / event.total) * 100));
		};
		xhr.onload = () => {
			lessonUploadXhr.current = null;
			if (xhr.status >= 200 && xhr.status < 300) {
				setIsUploadLessonStart(false);
				setLessonUploadBtnText('تحديث');
				setIsUploadLessonDialogOpen(false);
				window.location.reload();
				return;
			}
			// A rejected upload used to close the dialog and reload just like a
			// successful one, so failures looked identical to success.
			fail(xhr.status === 413 ? 'الملف كبير جدا' : `فشل رفع الملف (${xhr.status})`);
		};
		xhr.onerror = () => fail('انقطع الاتصال أثناء الرفع');
		xhr.onabort = () => fail('تم إلغاء الرفع');
		xhr.ontimeout = () => fail('انتهت مهلة الرفع');

		xhr.open('POST', url, true);
		// A multi-gigabyte upload on a slow line must not be killed by a timeout.
		xhr.timeout = 0;
		xhr.setRequestHeader('Content-Type', 'application/octet-stream');
		xhr.send(lessonContentFile);
	};

	const doCancelLessonUpload = () => lessonUploadXhr.current?.abort();

	// Show content
	const [isShowLessonContentDialogOpen, setIsShowLessonContentDialogOpen] = useState(false);
	const [lessonShowContentId, setLessonShowContentId] = useState(0);
	const [lessonShowContentTitle, setLessonShowContentTitle] = useState('');
	const [lessonShowContentType, setLessonShowContentType] = useState('فيديو');
	const [lessonShowContentUrl, setLessonShowContentUrl] = useState('');
	const doShowLessonContent = (id: number, title: string, type: string) => {
		setLessonShowContentId(id);
		setLessonShowContentTitle(title);
		setLessonShowContentType(type);
		setLessonShowContentUrl('/api/lessons/download/' + id + '/type/' + type);
		setIsShowLessonContentDialogOpen(true);
	};

	return (
		<div className="min-h-screen" style={{ backgroundColor: '#e6e6b8' }}>
			<div className="top-0 z-10000 w-full px-0 py-3 sm:p-6">
				<Navbar />
			</div>

			<div className="container mx-auto py-6 sm:py-8">
				<div className="text-center font-bold text-2xl sm:text-3xl">إدارة الدروس و الملخصات</div>
			</div>

			<div className="container mx-auto py-6 sm:py-8">
				{/* Filters */}
				<div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center">
					<div className="relative flex-1">
						<Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							placeholder="ابحث عن درس أو ملخص..."
							className="pr-10"
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							style={{ borderColor: 'green' }}
						/>
					</div>
					<div className="flex gap-4 flex-wrap">
						{levels.map((level) => (
							<Button key={level} size="sm" variant={selectedLevel === level ? 'default' : 'outline'} onClick={() => setSelectedLevel(level)}>
								{level}
							</Button>
						))}
					</div>
				</div>

				{/* Add Lesson Dialog */}
				<div>
					<Button size="sm" onClick={() => doOpenAddLessonDialog()}>
						إنشاء درس جديد
					</Button>
					<br />
					<br />
				</div>

				{/* Lessons Table */}
				{/* Ten columns never fit a phone: keep the cells legible and let the
				    wrapper scroll sideways instead of crushing them. */}
				<div className="overflow-x-auto rounded-sm" style={{ borderWidth: 1, borderColor: 'green' }}>
					<Table dir="rtl" className="min-w-[900px]">
						<TableCaption></TableCaption>
						<TableHeader>
							<TableRow>
								<TableHead className="font-bold text-foreground text-center">الرقم</TableHead>
								<TableHead className="font-bold text-foreground text-center">تاريخ التحميل</TableHead>
								<TableHead className="font-bold text-foreground text-center">العنوان</TableHead>
								<TableHead className="font-bold text-foreground text-center">تفصيل</TableHead>
								<TableHead className="font-bold text-foreground text-center">كلمات مفتاحية</TableHead>
								<TableHead className="font-bold text-foreground text-center">المستوى</TableHead>
								<TableHead className="font-bold text-foreground text-center">الشعبة</TableHead>
								<TableHead className="font-bold text-foreground text-center">المحتوى</TableHead>
								<TableHead className="font-bold text-foreground text-center">المدة</TableHead>
								<TableHead className="font-bold text-foreground text-center">#</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{filteredLessons.map((lesson, index) => (
								<TableRow key={lesson.id}>
									<TableCell className="text-center">{index + 1}</TableCell>
									<TableCell className="text-center">{format(new Date(lesson.create_at), 'yyyy-MM-dd')}</TableCell>
									<TableCell className="text-center">{lesson.title}</TableCell>
									<TableCell className="text-center">{lesson.description}</TableCell>
									<TableCell className="text-center">{lesson.keys_word}</TableCell>
									<TableCell className="text-center">{lesson.level}</TableCell>
									<TableCell className="text-center">{lesson.branch}</TableCell>
									<TableCell className="text-center">{lesson.type}</TableCell>
									<TableCell className="text-center">{lesson.duration} د</TableCell>
									<TableCell>
										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<Button variant="outline">...</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent>
												<DropdownMenuItem onClick={() => doShowLessonDetails(lesson.id)} dir="rtl">
													عرض التفاصيل
												</DropdownMenuItem>
												<DropdownMenuItem onClick={() => doShowLessonContent(lesson.id, lesson.title, lesson.type)} dir="rtl">
													عرض المحتوى
												</DropdownMenuItem>
												<DropdownMenuSeparator />
												<DropdownMenuItem onClick={() => doOpenUpdateLessonDialog(lesson.id)} dir="rtl">
													تحديث الدرس أو الملخص
												</DropdownMenuItem>
												<DropdownMenuItem onClick={() => doShowLessonUpload(lesson.id, lesson.title, lesson.type)} dir="rtl">
													تحديث المحتوى
												</DropdownMenuItem>
												<DropdownMenuSeparator />
												<DropdownMenuItem onClick={() => doShowDeleteLessonDialog(lesson.id)} style={{ color: 'red' }} dir="rtl">
													حدف
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
						<TableFooter></TableFooter>
					</Table>
				</div>
			</div>

			{/* Add lesson dialog */}
			<div dir="rtl">
				<Dialog open={isAddLessonDialogOpen} onOpenChange={setIsAddLessonDialogOpen}>
					<DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] max-w-[800px] overflow-y-auto p-4 sm:p-6" style={{ minHeight: 500 }} dir="rtl">
						<DialogHeader>
							<DialogTitle className="text-center" dir="rtl">
								إنشاء درس جديد
							</DialogTitle>
							<DialogDescription></DialogDescription>
						</DialogHeader>
						<form id="lesson-add-form" onSubmit={handleAddLessonSubmit} className="space-y-4">
							<div className="flex items-center justify-center bg-background p-2 w-full" dir="rtl">
								<div className="flex w-full min-w-0 flex-col gap-4 rounded-2xl border bg-card p-4 shadow-elevated sm:p-8">
									<div className="space-y-2">
										<Label htmlFor="title" className="font-bold">
											عنوان الدرس
										</Label>
										<Input
											id="title"
											placeholder="العنوان"
											value={lessonFormData.title}
											onChange={(e) => setLessonFormData({ ...lessonFormData, title: e.target.value })}
											required
										/>
									</div>

									<div className="space-y-2">
										<Label htmlFor="description" className="font-bold">
											التفصيل
										</Label>
										<Input
											id="description"
											placeholder="تفصيل"
											value={lessonFormData.description}
											onChange={(e) => setLessonFormData({ ...lessonFormData, description: e.target.value })}
											required
										/>
									</div>

									<div className="space-y-2">
										<Label htmlFor="keys_word" className="font-bold">
											الكلمات المفتاحية
										</Label>
										<Input
											id="keys_word"
											placeholder="كلمات مفتاحية"
											value={lessonFormData.keys_word}
											onChange={(e) => setLessonFormData({ ...lessonFormData, keys_word: e.target.value })}
											required
										/>
									</div>

									<div className="flex flex-col gap-4 sm:flex-row sm:gap-6">
										<div className="w-full min-w-0 space-y-2">
											<Label className="font-bold">المستوى الدراسي</Label>
											<Select
												value={lessonFormData.level}
												onValueChange={(value) => setLessonFormData({ ...lessonFormData, level: value })}
												dir="rtl">
												<SelectTrigger>
													<SelectValue placeholder="اختر المستوى" />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="1 ثانوي">السنة الأولى ثانوي</SelectItem>
													<SelectItem value="2 ثانوي">السنة الثانية ثانوي</SelectItem>
													<SelectItem value="3 ثانوي">السنة الثالثة ثانوي</SelectItem>
												</SelectContent>
											</Select>
										</div>

										<div className="w-full min-w-0 space-y-2">
											<Label className="font-bold">الشعبة</Label>
											<Select
												value={lessonFormData.branch}
												onValueChange={(value) => setLessonFormData({ ...lessonFormData, branch: value })}
												dir="rtl">
												<SelectTrigger>
													<SelectValue placeholder="اختر الشعبة" />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="علوم تجريبية">علوم تجريبية</SelectItem>
													<SelectItem value="رياضيات">رياضيات</SelectItem>
													<SelectItem value="تقني رياضي">تقني رياضي</SelectItem>
													<SelectItem value="تسيير واقتصاد">تسيير واقتصاد</SelectItem>
													<SelectItem value="آداب وفلسفة">آداب وفلسفة</SelectItem>
												</SelectContent>
											</Select>
										</div>
									</div>

									<div className="flex flex-col gap-4 sm:flex-row sm:gap-6">
										<div className="w-full min-w-0 space-y-2">
											<Label className="font-bold">المحتوى</Label>
											<Select
												value={lessonFormData.type}
												onValueChange={(value) => setLessonFormData({ ...lessonFormData, type: value })}
												dir="rtl">
												<SelectTrigger>
													<SelectValue placeholder="" />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="فيديو">فيديو</SelectItem>
													<SelectItem value="ملخص">ملخص</SelectItem>
												</SelectContent>
											</Select>
										</div>

										<div className="w-full min-w-0 space-y-2">
											<Label htmlFor="duration" className="font-bold">
												المدة (دقيقة)
											</Label>
											<Input
												id="duration"
												type="number"
												min={0}
												value={lessonFormData.duration}
												// Clearing the field yields '', and parseInt('') is NaN, which
												// serialises to null and writes a null duration.
												onChange={(e) => setLessonFormData({ ...lessonFormData, duration: Number.parseInt(e.target.value, 10) || 0 })}
												required
											/>
										</div>
									</div>
								</div>
							</div>
						</form>
						<DialogFooter className="sm:justify-start" dir="rtl">
							<Button type="submit" form="lesson-add-form" className="w-1/2" size="sm">
								إنشاء الدرس
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>

			{/* Update lesson dialog */}
			<div dir="rtl">
				<Dialog open={isUpdateLessonDialogOpen} onOpenChange={setIsUpdateLessonDialogOpen}>
					<DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] max-w-[800px] overflow-y-auto p-4 sm:p-6" style={{ minHeight: 400 }} dir="rtl">
						<DialogHeader>
							<DialogTitle className="text-center" dir="rtl">
								تحديث الدرس أو الملخص
							</DialogTitle>
							<DialogDescription></DialogDescription>
						</DialogHeader>
						{/* Was also id="lesson-add-form": two forms sharing one id means the
						    footer's form="..." can resolve to the wrong one. */}
						<form id="lesson-update-form" onSubmit={handleUpdateLessonSubmit} className="space-y-4">
							<div className="flex w-full items-center justify-center bg-background p-2 sm:p-4" dir="rtl">
								<div className="flex w-full min-w-0 flex-col gap-4 rounded-2xl border bg-card p-4 shadow-elevated sm:p-8">
									<div className="space-y-2">
										<Label htmlFor="title" className="font-bold">
											عنوان الدرس
										</Label>
										<Input
											id="title"
											placeholder="العنوان"
											value={lessonUpdate.title}
											onChange={(e) => setLessonUpdate({ ...lessonUpdate, title: e.target.value })}
											required
										/>
									</div>

									<div className="space-y-2">
										<Label htmlFor="description" className="font-bold">
											التفصيل
										</Label>
										<Input
											id="description"
											placeholder="تفصيل"
											value={lessonUpdate.description}
											onChange={(e) => setLessonUpdate({ ...lessonUpdate, description: e.target.value })}
											required
										/>
									</div>

									<div className="space-y-2">
										<Label htmlFor="keys_word" className="font-bold">
											الكلمات المفتاحية
										</Label>
										<Input
											id="keys_word"
											placeholder="كلمات مفتاحية"
											value={lessonUpdate.keys_word}
											onChange={(e) => setLessonUpdate({ ...lessonUpdate, keys_word: e.target.value })}
											required
										/>
									</div>

									<div className="flex flex-col gap-4 sm:flex-row sm:gap-6">
										<div className="w-full min-w-0 space-y-2">
											<Label className="font-bold">المستوى الدراسي</Label>
											<Select
												value={lessonUpdate.level}
												onValueChange={(value) => setLessonUpdate({ ...lessonUpdate, level: value })}
												dir="rtl">
												<SelectTrigger>
													<SelectValue placeholder="اختر المستوى" />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="1 ثانوي">السنة الأولى ثانوي</SelectItem>
													<SelectItem value="2 ثانوي">السنة الثانية ثانوي</SelectItem>
													<SelectItem value="3 ثانوي">السنة الثالثة ثانوي</SelectItem>
													<SelectItem value="1 متوسط">السنة الأولى متوسط</SelectItem>
													<SelectItem value="2 متوسط">السنة الثانية متوسط</SelectItem>
													<SelectItem value="3 متوسط">السنة الثالثة متوسط</SelectItem>
													<SelectItem value="4 متوسط">السنة الرابعة متوسط</SelectItem>
												</SelectContent>
											</Select>
										</div>

										<div className="w-full min-w-0 space-y-2">
											<Label className="font-bold">الشعبة</Label>
											<Select
												value={lessonUpdate.branch}
												onValueChange={(value) => setLessonUpdate({ ...lessonUpdate, branch: value })}
												dir="rtl">
												<SelectTrigger>
													<SelectValue placeholder="اختر الشعبة" />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="علوم تجريبية">علوم تجريبية</SelectItem>
													<SelectItem value="رياضيات">رياضيات</SelectItem>
													<SelectItem value="تقني رياضي">تقني رياضي</SelectItem>
													<SelectItem value="تسيير واقتصاد">تسيير واقتصاد</SelectItem>
													<SelectItem value="آداب وفلسفة">آداب وفلسفة</SelectItem>
												</SelectContent>
											</Select>
										</div>
									</div>

									<div className="flex flex-col gap-4 sm:flex-row sm:gap-6">
										<div className="w-full min-w-0 space-y-2">
											<Label className="font-bold">المحتوى</Label>
											<Select
												value={lessonUpdate.type}
												onValueChange={(value) => setLessonUpdate({ ...lessonUpdate, type: value })}
												dir="rtl">
												<SelectTrigger>
													<SelectValue placeholder="" />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="فيديو">فيديو</SelectItem>
													<SelectItem value="ملخص">ملخص</SelectItem>
												</SelectContent>
											</Select>
										</div>

										<div className="w-full min-w-0 space-y-2">
											<Label htmlFor="duration" className="font-bold">
												المدة (دقيقة)
											</Label>
											<Input
												id="duration"
												type="number"
												min={0}
												value={lessonUpdate.duration}
												onChange={(e) => setLessonUpdate({ ...lessonUpdate, duration: Number.parseInt(e.target.value, 10) || 0 })}
												required
											/>
										</div>
									</div>
								</div>
							</div>
						</form>
						<DialogFooter className="sm:justify-start" dir="rtl">
							<Button type="submit" form="lesson-update-form" className="w-1/2" size="sm">
								تحديث الدرس
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>

			{/* Upload lesson content dialog */}
			<div dir="rtl">
				{/* Closing the dialog mid-transfer would strand the request with no way
				    to see it finish or fail, so it only closes when idle. */}
				<Dialog
					open={isUploadLessonDialogOpen}
					onOpenChange={(open) => {
						if (!open && isUploadLessonStart) return;
						setIsUploadLessonDialogOpen(open);
					}}>
					<DialogContent dir="rtl">
						<DialogHeader>
							<DialogTitle></DialogTitle>
							<DialogDescription></DialogDescription>
						</DialogHeader>
						<div className="flex items-center justify-center bg-background p-2 sm:p-4" dir="rtl">
							<div className="w-full max-w-md">
								<div className="rounded-2xl border bg-card p-4 shadow-elevated sm:p-8">
									<h1 className="mb-2 text-center text-2xl font-bold text-foreground">تحديث المحتوى</h1>
									<br></br>
									<h2 className="mb-2 text-center text-foreground">{lessonUploadTitle}</h2>
									<br></br>
									<div className="space-y-2">
										<Label>المحتوى</Label>
										<Select value={lessonUploadType} onValueChange={(value) => setLessonUploadType(value)} dir="rtl">
											<SelectTrigger>
												<SelectValue placeholder="" />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="فيديو">فيديو</SelectItem>
												<SelectItem value="ملخص">ملخص</SelectItem>
											</SelectContent>
										</Select>
									</div>
									<br />
									<br />
									<input
										type="file"
										onChange={handleLessonContentChange}
										disabled={isUploadLessonStart}
										// Steers the picker at the right kind of file: the stored
										// extension follows the type above, so a PDF picked while
										// "فيديو" is selected would be saved as .mp4.
										accept={lessonUploadType === 'فيديو' ? 'video/*' : 'application/pdf'}
										className="block w-full max-w-full text-sm file:mr-3 file:cursor-pointer file:rounded file:border-0 file:bg-primary file:px-3 file:py-1 file:text-sm file:text-primary-foreground disabled:opacity-50"
									/>

									{lessonContentFile && !isUploadLessonStart && (
										<p className="mt-3 break-all text-sm text-muted-foreground">
											{lessonContentFile.name} — {formatBytes(lessonContentFile.size)}
										</p>
									)}

									{isUploadLessonStart && (
										<div className="mt-4 space-y-2">
											<Progress value={lessonUploadPercent} className="h-3" />
											<div className="flex items-center justify-between text-sm text-muted-foreground">
												<span className="font-bold text-foreground">{lessonUploadPercent}%</span>
												<span dir="ltr">
													{formatBytes(lessonUploadSentBytes)} / {formatBytes(lessonContentFile?.size ?? 0)}
												</span>
											</div>
											{lessonUploadPercent === 100 && <p className="text-sm text-muted-foreground">جاري الحفظ على الخادم ...</p>}
										</div>
									)}

									{lessonUploadError && <p className="mt-3 text-sm font-bold text-destructive">{lessonUploadError}</p>}
								</div>
							</div>
						</div>
						<DialogFooter className="sm:justify-start" dir="rtl">
							<div className="flex w-full flex-row gap-2">
								<Button className="w-1/2" size="sm" disabled={isUploadLessonStart} onClick={() => doUploadLessonContent()}>
									{lessonUploadBtnText}
								</Button>
								{isUploadLessonStart && (
									<Button className="w-1/2" size="sm" variant="outline" onClick={() => doCancelLessonUpload()}>
										إلغاء
									</Button>
								)}
							</div>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>

			{/* Show lesson content dialog */}
			<div dir="rtl">
				<Dialog open={isShowLessonContentDialogOpen} onOpenChange={setIsShowLessonContentDialogOpen}>
					<DialogContent className="flex h-[85vh] w-[calc(100%-1.5rem)] max-w-[900px] flex-col p-3 sm:p-6" dir="rtl">
						<DialogHeader className="shrink-0">
							<DialogTitle className="text-center" dir="rtl">
								{lessonShowContentTitle}
							</DialogTitle>
						</DialogHeader>
						{/* The viewer needs a real height to fill; percentage heights on the
						    old wrapper resolved against an auto-height parent and collapsed. */}
						<DialogDescription className="min-h-0 flex-1" dir="rtl">
							<object data={lessonShowContentUrl} className="h-full w-full" dir="rtl">
								المحتوى غير متوفر
							</object>
						</DialogDescription>
					</DialogContent>
				</Dialog>
			</div>

			{/* Lesson details dialog */}
			<div dir="rtl">
				<Dialog open={isLessonDetailsDialogOpen} onOpenChange={setIsLessonDetailsDialogOpen}>
					<DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] max-w-[800px] overflow-y-auto p-4 sm:p-6" style={{ minHeight: 400 }} dir="rtl">
						<DialogHeader>
							<DialogTitle className="text-center" dir="rtl">
								تفاصيل حول الدرس أو الملخص
							</DialogTitle>
							<DialogDescription></DialogDescription>
						</DialogHeader>
						<div className="flex items-center justify-center bg-background p-2 sm:p-4" dir="rtl">
							<div className="flex w-full min-w-0 flex-col gap-4 rounded-2xl border bg-card p-4 shadow-elevated sm:p-8">
								<div className="space-y-2">
									<Label htmlFor="title" className="font-bold">
										عنوان الدرس
									</Label>
									<Input id="title" value={lessonDetails.title!} readOnly />
								</div>

								<div className="flex flex-col gap-4 sm:flex-row sm:gap-6">
									<div className="w-full min-w-0 space-y-2">
										<Label htmlFor="description" className="font-bold">
											التفصيل
										</Label>
										<Input id="description" value={lessonDetails.description!} readOnly />
									</div>

									<div className="w-full min-w-0 space-y-2">
										<Label htmlFor="keys_word" className="font-bold">
											الكلمات المفتاحية
										</Label>
										<Input id="keys_word" value={lessonDetails.keys_word!} readOnly />
									</div>
								</div>

								<div className="flex flex-col gap-4 sm:flex-row sm:gap-6">
									<div className="w-full min-w-0 space-y-2">
										<Label htmlFor="level" className="font-bold">
											المستوى الدراسي
										</Label>
										<Input id="level" value={lessonDetails.level!} readOnly />
									</div>

									<div className="w-full min-w-0 space-y-2">
										<Label htmlFor="branch" className="font-bold">
											الشعبة
										</Label>
										<Input id="branch" value={lessonDetails.branch!} readOnly />
									</div>
								</div>

								<div className="flex flex-col gap-4 sm:flex-row sm:gap-6">
									<div className="w-full min-w-0 space-y-2">
										<Label htmlFor="type" className="font-bold">
											المحتوى
										</Label>
										<Input id="type" value={lessonDetails.type!} readOnly />
									</div>

									<div className="w-full min-w-0 space-y-2">
										<Label htmlFor="duration" className="font-bold">
											المدة (دقيقة)
										</Label>
										<Input id="duration" value={lessonDetails.duration!} readOnly />
									</div>
								</div>
							</div>
						</div>
						<DialogFooter className="sm:justify-start" dir="rtl">
							<Button size="sm" onClick={() => setIsLessonDetailsDialogOpen(false)}>
								موافق
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>

			{/* Lesson delete alert dialog */}
			<div dir="rtl">
				<AlertDialog open={isDeleteLessonDialogOpen} onOpenChange={setIsDeleteLessonDialogOpen}>
					<AlertDialogContent dir="rtl">
						<div dir="rtl">
							<AlertDialogHeader dir="rtl">
								<AlertDialogTitle>حدف درس أو ملخص</AlertDialogTitle>
							</AlertDialogHeader>
						</div>
						<AlertDialogFooter dir="rtl">
							<div className="flex flex-row gap-2">
								<Button size="sm" variant="outline" onClick={() => setIsDeleteLessonDialogOpen(false)}>
									الغاء
								</Button>
								<Button size="sm" variant="destructive" onClick={() => doDeleteLesson()}>
									أحدف
								</Button>
							</div>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</div>

			<Footer />
		</div>
	);
};

export default LessonsAdminComponent;
